import i18n from "@/i18n";

// Tenant-timezone-aware datetime helpers. Sessions are stored/served as UTC ISO strings;
// the UI renders them in the tenant's timezone (falls back to browser tz when unknown).

const cache = new Map<string, { fmt: Intl.DateTimeFormat }>();

function fmt(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
    const key = tz + "|" + JSON.stringify(opts);
    let hit = cache.get(key);
    if (!hit) {
        hit = { fmt: new Intl.DateTimeFormat("en-GB", { timeZone: tz, hour12: false, ...opts }) };
        cache.set(key, hit);
    }
    return hit.fmt;
}

function partsToMap(parts: Intl.DateTimeFormatPart[]): Record<string, string> {
    const m: Record<string, string> = {};
    for (const p of parts) m[p.type] = p.value;
    return m;
}

/** Format a UTC ISO string as "YYYY-MM-DD" in the tenant's timezone. */
export function formatSessionDate(utcIso: string, tz: string): string {
    const p = partsToMap(fmt(tz, { year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(utcIso)));
    return `${p.year}-${p.month}-${p.day}`;
}

/** Format a UTC ISO string as "HH:mm" in the tenant's timezone. */
export function formatSessionTime(utcIso: string, tz: string): string {
    const p = partsToMap(fmt(tz, { hour: "2-digit", minute: "2-digit" }).formatToParts(new Date(utcIso)));
    return `${p.hour}:${p.minute}`;
}

/** Return the ISO weekday (0=Mon..6=Sun) for a UTC instant as seen in `tz`. */
export function tzWeekdayMonFirst(utcIso: string, tz: string): number {
    const p = partsToMap(fmt(tz, { weekday: "short" }).formatToParts(new Date(utcIso)));
    // en-GB short weekdays: Mon Tue Wed Thu Fri Sat Sun
    const idx = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].indexOf(p.weekday || "Mon");
    return idx < 0 ? 0 : idx;
}

/** Offset (minutes) of `tz` relative to UTC at instant `at`. Positive → tz is ahead of UTC. */
function tzOffsetMinutes(tz: string, at: Date): number {
    const p = partsToMap(fmt(tz, { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }).formatToParts(at));
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return (asUtc - at.getTime()) / 60000;
}

/** Interpret `dateStr` (YYYY-MM-DD) + `timeStr` (HH:mm) as WALL-CLOCK in `tz` and return absolute UTC ISO. */
export function localDateTimeToUtcIso(dateStr: string, timeStr: string, tz: string): string {
    const [Y, M, D] = dateStr.split("-").map(Number);
    const [h, m] = timeStr.split(":").map(Number);
    // Guess by pretending wall-clock == UTC, measure tz offset at that instant, subtract.
    const guessUtc = Date.UTC(Y, M - 1, D, h, m, 0);
    const offset = tzOffsetMinutes(tz, new Date(guessUtc));
    // Adjust once — resolves for DST boundaries with 1-minute worst-case error in the ambiguous hour.
    const real = new Date(guessUtc - offset * 60000);
    return real.toISOString();
}

/** Today's date (YYYY-MM-DD) in the tenant's timezone. */
export function tzToday(tz: string): string {
    return formatSessionDate(new Date().toISOString(), tz);
}

/** Add `n` calendar days to a YYYY-MM-DD string (calendar arithmetic, timezone-agnostic). */
export function addDaysStr(dateStr: string, n: number): string {
    const [Y, M, D] = dateStr.split("-").map(Number);
    const d = new Date(Date.UTC(Y, M - 1, D));
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}

/** Locale-aware date+time formatter driven by i18next current language.
 *  Uses explicit patterns per language to guarantee deterministic output
 *  (avoids Intl fallbacks like ISO for "az"):
 *    az → dd.MM.yyyy HH:mm
 *    en → MM/DD/YYYY HH:mm
 *    ru → dd.mm.yyyy HH:mm
 *  Use everywhere instead of the parameter-less `new Date(x).toLocaleString()`. */
function pad2(n: number): string { return String(n).padStart(2, "0"); }

function langBase(): string {
    const l = (i18n.language || "az").toLowerCase();
    return l.split("-")[0];
}

export function fmtDate(iso: string | number | Date | null | undefined): string {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return String(iso);
        const dd = pad2(d.getDate());
        const mm = pad2(d.getMonth() + 1);
        const yyyy = d.getFullYear();
        const L = langBase();
        if (L === "en") return `${mm}/${dd}/${yyyy}`;
        // az, ru → dd.MM.yyyy
        return `${dd}.${mm}.${yyyy}`;
    } catch { return String(iso); }
}

export function fmtTime(iso: string | number | Date | null | undefined): string {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (isNaN(d.getTime())) return String(iso);
        return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
    } catch { return String(iso); }
}

export function fmtDateTime(iso: string | number | Date | null | undefined): string {
    if (!iso) return "—";
    const dateStr = fmtDate(iso);
    if (dateStr === "—" || dateStr === String(iso)) return dateStr;
    return `${dateStr} ${fmtTime(iso)}`;
}
